import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import DeviceMap from "../Device/DeviceMap";
import { API_BASE_URL } from "../../config";

// The "Proposed Rate" column here was a free-text number the customer could
// edit, and the checkout totalled it. The server prices every booking as
// `Device.daily_rate x days` and ignores the submitted figure entirely, so the
// cart could quote one price while the card was charged another. (Before that
// was fixed server-side, the same field was the price -- POSTing 0.01 bought a
// screen-day for a cent.)
//
// The rate, the day count and the line total now all come from the server on
// the cart response, computed by the same code that will do the charging.

function money(value) {
    if (value === null || value === undefined) return "—";
    const n = Number(value);
    return isNaN(n) ? "—" : `$${n.toFixed(2)}`;
}

function forInput(value) {
    // <input type="datetime-local"> needs YYYY-MM-DDTHH:mm and rejects the
    // seconds and timezone an ISO string carries.
    if (!value) return "";
    const d = new Date(value);
    if (isNaN(d)) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function readable(value) {
    if (!value) return "—";
    const d = new Date(value);
    return isNaN(d) ? "—" : d.toLocaleDateString();
}

// How the chosen ad fits this screen, from the server (utils/fit.py). A
// warning, never a block: the line stays bookable. Only shown when it will not
// fill the screen -- "fits" on every line would be noise, and "unknown" means
// the host has not said which way up the screen is, which is not
// something the advertiser can act on.
function fitNote(item) {
    const fit = item && item.fit;
    if (!fit || (fit.fit !== "poor" && fit.fit !== "letterboxed")) return null;
    const where = item.placement_label ? `${item.placement_label}: ` : "";
    return `${where}${fit.message}`;
}

export default function DeviceRequestCart() {
    const [cart, setCart] = useState([]);
    const [ads, setAds] = useState([]);
    const [status, setStatus] = useState("");
    const [editingId, setEditingId] = useState(null);
    const [editData, setEditData] = useState({});
    const navigate = useNavigate();

    const token = localStorage.getItem("token");

    const loadCart = useCallback(async () => {
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer/cart`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            setCart(Array.isArray(data) ? data : []);
        } catch {
            setCart([]);
        }
    }, [token]);

    useEffect(() => {
        loadCart();
        fetch(`${API_BASE_URL}/api/customer/ads`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then((res) => res.json())
            .then((data) => setAds(data.images || []))
            .catch(() => setAds([]));
    }, [loadCart, token]);

    async function handleRemove(itemId) {
        setStatus("Removing…");
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer/cart/${itemId}`, {
                method: "DELETE",
                headers: { Authorization: `Bearer ${token}` },
            });
            if (res.ok) {
                setCart((current) => current.filter((item) => item.id !== itemId));
                setStatus("Removed.");
            } else {
                setStatus("Could not remove that item.");
            }
        } catch {
            setStatus("Network error.");
        }
    }

    function handleEdit(item) {
        setEditingId(item.id);
        setEditData({
            start_time: forInput(item.start_time),
            end_time: forInput(item.end_time),
            image_id: item.image_id || "",
        });
        setStatus("");
    }

    async function handleSave(item) {
        setStatus("Saving…");
        try {
            const res = await fetch(`${API_BASE_URL}/api/customer/cart/${item.id}`, {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(editData),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setStatus(data.message || "Could not save those changes.");
                return;
            }
            // Take the server's row wholesale: it carries the re-priced total
            // for the new dates, which the page cannot work out on its own.
            setCart((current) => current.map((c) => (c.id === item.id ? data : c)));
            setEditingId(null);
            setEditData({});
            setStatus("Saved.");
        } catch {
            setStatus("Network error.");
        }
    }

    // Not pre-filtered on coordinates any more: DeviceMap drops the ones it
    // cannot place and says how many, which is more useful than them silently
    // not being there.
    const cartDevices = cart.map((item) => ({
        id: item.device_id,
        name: item.business_name || "Device",
        latitude: item.latitude,
        longitude: item.longitude,
        address: item.address,
        daily_rate: item.daily_rate,
    }));

    const total = cart.reduce((sum, item) => sum + (Number(item.line_total) || 0), 0);
    const blocked = cart.filter((item) => !item.bookable);

    return (
        <div>
            <h2>Your cart</h2>
            {/* No geolocation here: the cart's own screens are the point. */}
            <DeviceMap devices={cartDevices} locate={false} height={280} />

            {cart.length === 0 ? (
                <div style={{ marginTop: 16 }}>No screens in your cart.</div>
            ) : (
                <>
                    <div style={{ overflowX: "auto", marginTop: 16 }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ background: "#ea476e22" }}>
                                    <th style={{ padding: "0.5rem", textAlign: "left" }}>Screen</th>
                                    <th style={{ padding: "0.5rem", textAlign: "left" }}>Location</th>
                                    <th style={{ padding: "0.5rem", textAlign: "left" }}>Dates</th>
                                    <th style={{ padding: "0.5rem", textAlign: "left" }}>Ad</th>
                                    <th style={{ padding: "0.5rem", textAlign: "right" }}>Rate</th>
                                    <th style={{ padding: "0.5rem", textAlign: "right" }}>Hours</th>
                                    <th style={{ padding: "0.5rem", textAlign: "right" }}>Total</th>
                                    <th style={{ padding: "0.5rem" }}></th>
                                </tr>
                            </thead>
                            <tbody>
                                {cart.map((item) => {
                                    const editing = editingId === item.id;
                                    const ad = ads.find((a) => a.id === Number(editing ? editData.image_id : item.image_id));
                                    return (
                                        <React.Fragment key={item.id}>
                                            <tr style={{ borderBottom: item.issue || fitNote(item) ? "none" : "1px solid #eee" }}>
                                                <td style={{ padding: "0.5rem" }}>
                                                    {item.business_name || `Screen #${item.device_id}`}
                                                </td>
                                                <td style={{ padding: "0.5rem" }}>{item.address || "—"}</td>

                                                {editing ? (
                                                    <td style={{ padding: "0.5rem" }}>
                                                        <input
                                                            type="datetime-local"
                                                            value={editData.start_time}
                                                            onChange={(e) => setEditData({ ...editData, start_time: e.target.value })}
                                                        />
                                                        <input
                                                            type="datetime-local"
                                                            value={editData.end_time}
                                                            onChange={(e) => setEditData({ ...editData, end_time: e.target.value })}
                                                            style={{ marginTop: 4 }}
                                                        />
                                                    </td>
                                                ) : (
                                                    <td style={{ padding: "0.5rem" }}>
                                                        {readable(item.start_time)} – {readable(item.end_time)}
                                                    </td>
                                                )}

                                                <td style={{ padding: "0.5rem" }}>
                                                    {editing ? (
                                                        <select
                                                            value={editData.image_id || ""}
                                                            onChange={(e) => setEditData({ ...editData, image_id: e.target.value })}
                                                        >
                                                            <option value="">Choose an ad</option>
                                                            {ads.map((a) => (
                                                                <option key={a.id} value={a.id}>{a.name || `Ad #${a.id}`}</option>
                                                            ))}
                                                        </select>
                                                    ) : ad ? (
                                                        <img
                                                            src={ad.thumbnail_url}
                                                            alt={ad.name || "Ad"}
                                                            style={{ width: 40, height: 40, objectFit: "cover" }}
                                                        />
                                                    ) : (
                                                        <span style={{ color: "#ea476e" }}>None</span>
                                                    )}
                                                </td>

                                                {/* All three come from the server, computed the same
                                                    way the charge will be. */}
                                                <td style={{ padding: "0.5rem", textAlign: "right" }}>
                                            {item.hourly_rate || money(item.daily_rate)}
                                            <div style={{ color: "#888", fontSize: "0.8rem" }}>
                                                per hour
                                            </div>
                                        </td>
                                                <td style={{ padding: "0.5rem", textAlign: "right" }}>{item.hours ?? "—"}</td>
                                                <td style={{ padding: "0.5rem", textAlign: "right", fontWeight: 600 }}>
                                                    {money(item.line_total)}
                                                </td>

                                                <td style={{ padding: "0.5rem", whiteSpace: "nowrap" }}>
                                                    {editing ? (
                                                        <>
                                                            <button onClick={() => handleSave(item)}>Save</button>
                                                            <button
                                                                onClick={() => { setEditingId(null); setEditData({}); }}
                                                                style={{ marginLeft: 6 }}
                                                            >
                                                                Cancel
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <button onClick={() => handleEdit(item)}>Edit</button>
                                                            <button onClick={() => handleRemove(item.id)} style={{ marginLeft: 6 }}>
                                                                Remove
                                                            </button>
                                                        </>
                                                    )}
                                                </td>
                                            </tr>
                                            {item.issue && (
                                                <tr style={{ borderBottom: fitNote(item) ? "none" : "1px solid #eee" }}>
                                                    <td colSpan={8} style={{ padding: "0 0.5rem 0.5rem", color: "#ea476e", fontSize: "0.9rem" }}>
                                                        {item.issue}
                                                    </td>
                                                </tr>
                                            )}
                                            {fitNote(item) && (
                                                <tr style={{ borderBottom: "1px solid #eee" }}>
                                                    <td colSpan={8} style={{ padding: "0 0.5rem 0.5rem", color: "#8a5a00", fontSize: "0.9rem" }}>
                                                        {fitNote(item)}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td colSpan={6} style={{ padding: "0.75rem 0.5rem", textAlign: "right", fontWeight: 600 }}>
                                        Total
                                    </td>
                                    <td style={{ padding: "0.75rem 0.5rem", textAlign: "right", fontWeight: 700 }}>
                                        {money(total)}
                                    </td>
                                    <td></td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {blocked.length > 0 && (
                        <div style={{ color: "#ea476e", marginTop: 16 }}>
                            {blocked.length === 1
                                ? "One screen in your cart cannot be booked yet — see the note above."
                                : `${blocked.length} screens in your cart cannot be booked yet — see the notes above.`}
                        </div>
                    )}

                    <div style={{ marginTop: 24 }}>
                        <button
                            onClick={() => navigate("/checkout")}
                            disabled={blocked.length > 0}
                            style={{
                                padding: "8px 24px",
                                fontSize: "1rem",
                                background: blocked.length > 0 ? "#ccc" : "#ea476e",
                                color: "#fff",
                                border: "none",
                                borderRadius: 4,
                                cursor: blocked.length > 0 ? "default" : "pointer",
                            }}
                        >
                            Checkout
                        </button>
                    </div>
                </>
            )}
            <div style={{ marginTop: 16, color: "#888" }}>{status}</div>
        </div>
    );
}
